import React, { useState, useEffect, useContext } from 'react';
import {
  Tabs, Tab, Box, Typography, Card, CardContent, Button, Chip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CodeRunner from '../components/CodeRunner';
import { content, progress as progressApi } from '../api/client';

const NextLessonButton = ({ moduleId, courseId, lessonId, onNavigate }) => {
  const [isLastLesson, setIsLastLesson] = useState(false);

  useEffect(() => {
    content.modules()
      .then(({ data }) => {
        const mod = data.find(m => m.id === moduleId);
        const course = mod?.courses?.find(c => c.id === courseId);
        const lessons = course?.lessons || [];
        const idx = lessons.findIndex(l => l.id === lessonId);
        setIsLastLesson(idx === lessons.length - 1);
      });
  }, [moduleId, courseId, lessonId]);

  return (
    <Box>
      <Button variant="contained" color="success" onClick={onNavigate} size="large">
        {isLastLesson ? 'Take Course Quiz →' : 'Go to Next Lesson →'}
      </Button>
      {isLastLesson && (
        <Box mt={1}>
          <Chip label="Last lesson — quiz next!" color="warning" size="small" />
        </Box>
      )}
    </Box>
  );
};

const LessonTemplate = () => {
  const { moduleId, courseId, lessonId } = useParams();
  const [tab, setTab] = useState(0);
  const [subTab, setSubTab] = useState(0);
  const [lessonContent, setLessonContent] = useState(null);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [lessonTitle, setLessonTitle] = useState('');

  const handleMainTabChange = (_, newValue) => setTab(newValue);
  const handleSubTabChange = (_, newValue) => setSubTab(newValue);

  useEffect(() => {
    content.lesson(moduleId, courseId, lessonId)
      .then(({ data }) => setLessonContent(data))
      .catch(() => setLessonContent(null));
  }, [moduleId, courseId, lessonId]);

  useEffect(() => {
    content.modules()
      .then(({ data }) => {
        const module = data.find(m => m.id === moduleId);
        const course = module?.courses?.find(c => c.id === courseId);
        const lesson = course?.lessons?.find(l => l.id === lessonId);
        if (lesson) setLessonTitle(lesson.title);
      });
  }, [moduleId, courseId, lessonId]);

  const markLessonComplete = () => {
    if (!user?.id) return;
    progressApi.update({ course_id: courseId, lesson_id: lessonId, completed: true }).catch(() => {});
  };

  const renderMarkdown = (text) => {
    if (!text || typeof text !== 'string') {
      return <Typography>No content available</Typography>;
    }

    return (
      <ReactMarkdown
        children={text}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
          ),
          strong: ({ children }) => <>{children}</>,
          ul: ({ children }) => <ul style={{ paddingLeft: '1.5rem' }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ paddingLeft: '1.5rem' }}>{children}</ol>,
          p: ({ children }) => {
            const rawText = Array.isArray(children)
              ? children.map(c => typeof c === 'string' ? c : c?.props?.children || '').join('')
              : (typeof children === 'string' ? children : '');
            const isReflection = rawText.toLowerCase().includes('reflection questions');
            return (
              <Typography
                variant="body1"
                paragraph
                className={isReflection ? 'reflection-block' : ''}
              >
                {children}
              </Typography>
            );
          },
          h1: ({ children }) => <Typography variant="h4" gutterBottom>{children}</Typography>,
          h2: ({ children }) => <Typography variant="h5" gutterBottom>{children}</Typography>,
          h3: ({ children }) => <Typography variant="h6" gutterBottom>{children}</Typography>,
          code({ inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={materialLight}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>{children}</code>
            );
          }
        }}
      />
    );
  };

  const renderGlossary = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    return (
      <Box mt={3}>
        <div className="glossary-block">
          <Typography variant="subtitle1" gutterBottom>📘 Terms in this section:</Typography>
          <ul>
            {items.map((entry, i) => (
              <li key={i}>
                <strong>{entry?.term || 'Unknown term'}:</strong> {entry?.definition || 'No definition available'}
              </li>
            ))}
          </ul>
        </div>
      </Box>
    );
  };

  const tabLabels = ["Overview", "Learn", "Try It", "Challenge", "Wrap-Up"];

  const {
    overview = '',
    learnTabs = [],
    tryIt = '',
    challenge = '',
    wrapUp = '',
    wrapGlossary = []
  } = lessonContent || {};

  const safeLearnTabs = Array.isArray(learnTabs) ? learnTabs : [];
  const safeWrapGlossary = Array.isArray(wrapGlossary) ? wrapGlossary : [];

  const handleNextTab = () => {
    if (tab === 1 && subTab < safeLearnTabs.length - 1) {
      setSubTab(prev => prev + 1);
    } else if (tab === 1 && subTab === safeLearnTabs.length - 1) {
      setTab(prev => prev + 1);
      setSubTab(0);
    } else if (tab < tabLabels.length - 1) {
      setTab(prev => prev + 1);
    }
  };

  const handlePrevTab = () => {
    if (tab === 1 && subTab > 0) {
      setSubTab(prev => prev - 1);
    } else if (tab > 0) {
      setTab(prev => prev - 1);
      if (tab === 2 && safeLearnTabs.length > 0) {
        setSubTab(safeLearnTabs.length - 1);
      }
    }
  };

  const goToNextLesson = () => {
    markLessonComplete();
    content.modules()
      .then(({ data }) => {
        const module = data.find(m => m.id === moduleId);
        const course = module?.courses?.find(c => c.id === courseId);
        const lessons = course?.lessons;
        const currentIndex = lessons.findIndex(l => l.id === lessonId);
        const nextLesson = lessons[currentIndex + 1];
        if (nextLesson) {
          setTab(0);
          setSubTab(0);
          navigate(`/module/${moduleId}/course/${courseId}/lesson/${nextLesson.id}`);
        } else {
          navigate(`/module/${moduleId}/course/${courseId}/quiz`);
        }
      });
  };

  if (!lessonContent) {
    return <Box p={2}><Typography>Loading lesson...</Typography></Box>;
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom align="center">
        {lessonTitle || "Untitled Lesson"}
      </Typography>

      <Tabs value={tab} onChange={handleMainTabChange} centered>
        {tabLabels.map(label => <Tab key={label} label={label} />)}
      </Tabs>

      {tab === 0 && (
        <Box p={3}><Card><CardContent>{renderMarkdown(overview)}</CardContent></Card></Box>
      )}

      {tab === 1 && (
        <Box>
          <Tabs value={subTab} onChange={handleSubTabChange} variant="scrollable" scrollButtons="auto">
            {safeLearnTabs.map((t, i) => (
              <Tab key={i} label={t.label || `Tab ${i + 1}`} />
            ))}
          </Tabs>
          <Box p={3}>
            <Card><CardContent>
              {safeLearnTabs[subTab] && (
                <>
                  {renderMarkdown(safeLearnTabs[subTab]?.content)}
                  {safeLearnTabs[subTab]?.editor && (
                    <Box mt={3}>
                      <Typography variant="subtitle1" gutterBottom>🧪 Try it yourself:</Typography>
                      <CodeRunner
  defaultCode=""
  autoDetect={true}
  storageKey={`lesson-${lessonId}-tab-${subTab}`}
  resetTrigger={`${lessonId}-learn-${subTab}`}
/>
                    </Box>
                  )}
                  {renderGlossary(safeLearnTabs[subTab]?.glossary)}
                </>
              )}
            </CardContent></Card>
          </Box>
        </Box>
      )}

      {tab === 2 && (
        <Box p={3}><Card><CardContent>
          {renderMarkdown(tryIt)}
          <Box mt={3}>
            <CodeRunner
  defaultCode=""
  autoDetect={true}
  storageKey={`lesson-${lessonId}-tryit`}
  resetTrigger={`${lessonId}-tryit`}
/>
          </Box>
        </CardContent></Card></Box>
      )}

      {tab === 3 && (
        <Box p={3}><Card><CardContent>
          {renderMarkdown(challenge)}
          <Box mt={3}>
            <CodeRunner
  defaultCode=""
  autoDetect={true}
  storageKey={`lesson-${lessonId}-challenge`}
  resetTrigger={`${lessonId}-challenge`}
/>
          </Box>
        </CardContent></Card></Box>
      )}

      {tab === 4 && (
        <Box p={3}>
          <Card><CardContent>
            {renderMarkdown(wrapUp)}
            {renderGlossary(safeWrapGlossary)}
          </CardContent></Card>
          <Box textAlign="center" mt={2}>
            <NextLessonButton
              moduleId={moduleId}
              courseId={courseId}
              lessonId={lessonId}
              onNavigate={goToNextLesson}
            />
          </Box>
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" mt={3} px={2}>
        <Button
          variant="outlined"
          disabled={tab === 0 && subTab === 0}
          onClick={handlePrevTab}
        >
          ← Back
        </Button>
        <Button
          variant="outlined"
          disabled={tab === tabLabels.length - 1}
          onClick={handleNextTab}
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default LessonTemplate;
